import { getOffers } from '@api/offers/apiOffers';
import { useLoading } from '@composables/useLoading.ts';
import type { SerializedOffer } from '@models/offer.ts';
import { OfficeStateEnum } from '@organisms/offer/office-state-enum';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useAddressFilterStore } from './useAddressFilterStore.ts';

export enum OfferListViewMode {
  mosaic = 'mosaic',
  list = 'list',
}

export enum OfferListSortMode {
  updateDesc = 'update-desc',
  priceDesc = 'price-desc',
  priceAsc = 'price-asc',
  capacityDesc = 'capacity-desc',
  capacityAsc = 'capacity-asc',
  addressOfficeNumber = 'address-office-number',
}

interface UrlParsedParams {
  view: OfferListViewMode | null;
  sort: OfferListSortMode | null;
  search: string | null;
  states: string[] | null;
}

export const useOfferListStore = defineStore('offerList', () => {
  const { isLoading, startLoading, stopLoading } = useLoading();
  const isLoadingNext = ref<boolean>(false);

  const urlParams = computed<UrlParsedParams>({
    get: (): UrlParsedParams => {
      const params: URLSearchParams = new URLSearchParams(window.location.search);
      return {
        view: params.get('view') as OfferListViewMode | null,
        sort: params.get('sort') as OfferListSortMode | null,
        search: params.get('search'),
        states: params.get('states')?.split(',') as string[] | null,
      } as UrlParsedParams;
    },
    set: (params: UrlParsedParams) => {
      const url = new URL(window.location.href);
      const { searchParams } = url;
      params.view ? searchParams.set('view', params.view) : searchParams.delete('view');
      params.sort ? searchParams.set('sort', params.sort) : searchParams.delete('sort');
      params.search ? searchParams.set('search', params.search) : searchParams.delete('search');
      (params.states && params.states.length > 0) ? searchParams.set('states', params.states.join(',')) : searchParams.delete('states');
      window.history.replaceState({}, '', url.toString().replace(/%2C/g, ','));
    },
  });

  const offers = ref<SerializedOffer[]>([]);
  const total = ref<number>(0);

  const currentPage = ref<number>(1);
  const hasNextPage = computed<boolean>(() => total.value > offers.value.length);

  const viewMode = ref<OfferListViewMode>(urlParams.value.view ? urlParams.value.view : OfferListViewMode.mosaic);
  const sortMode = ref<OfferListSortMode>(urlParams.value.sort ? urlParams.value.sort : OfferListSortMode.updateDesc);

  watch(viewMode, (newViewMode: OfferListViewMode): void => {
    if (newViewMode === OfferListViewMode.mosaic) {
      sortMode.value = OfferListSortMode.updateDesc;
      return;
    }
    sortMode.value = OfferListSortMode.addressOfficeNumber;
  });

  const filterableStates: string[] = [
    OfficeStateEnum.PUBLISHED,
    OfficeStateEnum.UNPUBLISHED,
    OfficeStateEnum.MODERATION,
    OfficeStateEnum.OFFMARKET,
    OfficeStateEnum.DRAFT,
    OfficeStateEnum.BLOCKED,
  ];

  const filterStates = ref<string[]>(urlParams.value.states ? urlParams.value.states : []);

  const filterTitle = ref<string>(urlParams.value.search ? urlParams.value.search : '');

  const { selectedAddressesIds } = storeToRefs(useAddressFilterStore());

  const queryParams = computed<URLSearchParams>(() => {
    const params = new URLSearchParams();
    if (filterStates.value.length) {
      params.append('states', filterStates.value.join(','));
    }

    if (selectedAddressesIds.value.length) {
      params.append('addresses', selectedAddressesIds.value.join(','));
    }

    if (filterTitle.value.length) {
      params.append('search', filterTitle.value);
    }

    params.append('sort', sortMode.value);
    params.append('view', viewMode.value);

    return params;
  });

  const load = async (next?: boolean): Promise<void> => {
    if (isLoading.value) {
      return;
    }

    startLoading();
    isLoadingNext.value = !!next;

    const params = queryParams.value;

    if (next) {
      if (!hasNextPage.value) {
        stopLoading();
        return;
      }
      currentPage.value += 1;
      params.set('page', currentPage.value.toString());
    } else {
      currentPage.value = 1;
    }

    const response = await getOffers(params);
    if (response) {
      total.value = response.total;
      offers.value = next ? [ ...offers.value, ...response.offers ] : response.offers;
    }

    stopLoading();
    isLoadingNext.value = false;
  };

  watch(queryParams, () => load());

  const updateUrlParams = (): void => {
    urlParams.value = {
      view: viewMode.value,
      sort: sortMode.value,
      search: filterTitle.value,
      states: filterStates.value,
    };
  };

  watch(
    [ viewMode, sortMode, filterTitle, filterStates ],
    () => {
      updateUrlParams();
    },
  );

  return {
    isLoading,
    isLoadingNext,
    viewMode,
    sortMode,
    offers,
    total,
    load,
    filterableStates,
    filterStates,
    filterTitle,
    hasNextPage,
  };
});
